import React, { useState, useEffect } from 'react';
import { Scale } from 'lucide-react';
import { CommonInput } from '@/components/ui/common-input';
import { CommonButton } from '@/components/ui/common-button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

const LogGateInDialog = ({ open, onOpenChange, trip, submitting, onSubmit }) => {
  const [receivedQty, setReceivedQty] = useState('');
  const [missingWagons, setMissingWagons] = useState('');
  const [remarks, setRemarks] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open && trip) {
      setReceivedQty('');
      setMissingWagons('');
      setRemarks('');
      setErrors({});
    }
  }, [open, trip]);

  const baselineQty = trip?.baseline_qty_mt || trip?.total_dispatched_qty_mt || 0;
  const liveVariance = receivedQty ? parseFloat(receivedQty) - baselineQty : 0;
  const liveVarianceType = liveVariance < 0 ? 'SHORTAGE' : liveVariance > 0 ? 'EXCESS' : 'BALANCED';
  const showLiveVariance = receivedQty && !isNaN(parseFloat(receivedQty));

  const validate = () => {
    const newErrors = {};
    if (receivedQty) {
      const qty = parseFloat(receivedQty);
      if (isNaN(qty) || qty <= 0) {
        newErrors.receivedQty = 'Quantity must be a positive number';
      }
    }

    if (missingWagons !== '') {
      const wagons = parseInt(missingWagons);
      if (isNaN(wagons) || wagons < 0) {
        newErrors.missingWagons = 'Missing wagons must be a non-negative integer';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    onSubmit({
      receipt_qty: receivedQty ? parseFloat(receivedQty) : null,
      missing_wagons: missingWagons !== '' ? parseInt(missingWagons) : 0,
      remarks: remarks.trim() || null
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[620px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-foreground">
              Log Gate-In Receipt
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="border border-border p-3.5 rounded-lg bg-card grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground block text-xs font-semibold">Trip Number</span>
                <span className="font-semibold text-foreground">{trip?.trip_number}</span>
              </div>
              <div>
                <span className="text-muted-foreground block text-xs font-semibold">RR Number</span>
                <span className="font-semibold text-foreground">{trip?.rr_number}</span>
              </div>
              <div className="pt-2 border-t">
                <span className="text-muted-foreground block text-xs font-semibold">Origin</span>
                <span className="font-semibold text-foreground text-xs">{trip?.rake_point_name || trip?.rake_point?.name}</span>
              </div>
              <div className="pt-2 border-t">
                <span className="text-muted-foreground block text-xs font-semibold">Baseline (Dispatched)</span>
                <span className="font-bold text-primary">{parseFloat(baselineQty).toFixed(2)} MT</span>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <CommonInput
                label="Received Gate-In Qty (MT)"
                id="receivedQty"
                type="number"
                step="0.01"
                value={receivedQty}
                onChange={(e) => setReceivedQty(e.target.value)}
                error={errors.receivedQty}
                placeholder="e.g. 10"
              />

              {showLiveVariance && (
                <div className={`py-1 px-2.5 rounded text-xs font-medium flex justify-between items-center transition-all duration-200 ${liveVarianceType === 'EXCESS'
                  ? "bg-amber-50 border border-amber-200 text-amber-800 dark:bg-amber-950/20 dark:border-amber-900/50 dark:text-amber-300"
                  : liveVarianceType === 'SHORTAGE'
                    ? "bg-red-50 border border-red-200 text-red-800 dark:bg-red-950/20 dark:border-red-900/50 dark:text-red-300"
                    : "bg-emerald-50 border border-emerald-200 text-emerald-800 dark:bg-emerald-950/20 dark:border-emerald-900/50 dark:text-emerald-300"
                  }`}>
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <Scale className="w-3.5 h-3.5" /> Weight Difference (Sent vs Received):
                  </span>
                  <span className="font-semibold">
                    {liveVariance > 0 ? `+${liveVariance.toFixed(2)}` : liveVariance.toFixed(2)} MT {
                      liveVarianceType === 'EXCESS' ? '(Extra weight arrived / EXCESS)' :
                        liveVarianceType === 'SHORTAGE' ? '(Less weight arrived / SHORTAGE)' : '(Perfect weight / BALANCED)'
                    }
                  </span>
                </div>
              )}

              <CommonInput
                label="Missing Wagons"
                id="missingWagons"
                type="number"
                value={missingWagons}
                onChange={(e) => setMissingWagons(e.target.value)}
                error={errors.missingWagons}
                placeholder="e.g. 0"
              />



              <CommonInput
                label="Remarks"
                id="remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="e.g. 2 wagons short; received under seal check"
              />
            </div>
          </div>

          <DialogFooter>
            <CommonButton type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </CommonButton>
            <CommonButton type="submit" loading={submitting}>
              Complete Gate-in
            </CommonButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LogGateInDialog;
